import { useQueryClient } from '@tanstack/react-query';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import {
  CompanyAllFreqFilter,
  CompanyAllPlanFilter,
  CompanyOrder,
} from '../../types/company.types';
import { OfferSortBy, SubscriptionOffer } from '../../types/offer.types';
import { useAppQuery } from '../use-app-query.hook';

type GetSubscriptionOffersResponse = {
  count: number;
  offers: SubscriptionOffer[];
  totalPages: number;
};

// tenant: global — offers exist before any company is created, so they are a super-admin resource
// listed on the Companies page rather than a companies/:companyId sub-resource.
export const useSubscriptionOffers = ({
  take = 10,
  currentPage = 1,
  search,
  planType,
  planFrequency,
  orderBy,
  orderMode,
  enabled = true,
}: {
  currentPage?: number;
  enabled?: boolean;
  orderBy?: OfferSortBy;
  orderMode?: CompanyOrder;
  planFrequency?: CompanyAllFreqFilter;
  planType?: CompanyAllPlanFilter;
  search?: string;
  take?: number;
}) => {
  const queryClient = useQueryClient();

  const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * take;

  const filterParams = {
    planType: planType && planType !== 'all' ? planType : undefined,
    planFrequency: planFrequency && planFrequency !== 'all' ? planFrequency : undefined,
    orderBy: orderBy ?? undefined,
    orderMode: orderBy ? (orderMode ?? undefined) : undefined,
  };

  const { data } = useAppQuery<GetSubscriptionOffersResponse>({
    queryKey: [
      QueryKey.GET_SUBSCRIPTION_OFFERS,
      { page: currentPage, take, search, ...filterParams },
    ],
    queryFn: () =>
      ApiService.get<GetSubscriptionOffersResponse>(
        { endpoint: BackendRoute.SUBSCRIPTION_OFFERS },
        { queryParams: { take, skip, search, ...filterParams } },
      ),
    enabled,
  });

  const invalidateQuery = () => {
    void queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SUBSCRIPTION_OFFERS] });
  };

  return {
    invalidateQuery,
    offers: data?.offers || [],
    totalPages: data?.totalPages || 0,
    totalCount: data?.count || 0,
  };
};
